import React, { useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import i18n from 'live/services/i18n'
import globalNotification from 'live/services/global-notification'
import Loading, { LoadingOverlay } from 'live/components/ui/Loading'

import ImportData from './ImportData'
import TemporaryDataTable from './TemporaryDataTable'
import { TemporaryDataType, TemporarySetDataType } from './temporary-data-table/useTemporaryDataState'
import { GlobalNotification } from './global-components/GlobalNotification'
import {
    saveAllTemporaryData,
    transferDraftTableToStaticDataDashboard,
    validateEntityByHierarchy,
    validateLocalFieldsByEntity
} from './importer-api'
import { DataErrorsSidebar } from './global-components/DataErrorsSidebar'
import { DecisionModal } from './temporary-data-table/components/DecisionModal'
import { NoData } from './temporary-data-table/components/NoData'
import { createWellOnCodaf, deleteWellFromCodaf, fetchCodafEntity, updateWellFromCodaf } from './services/api'
import ExportStaticDataModal from '../exporter/ExportStaticDataModal'
import { StaticDataImporterContext } from './context/StaticDataImporterContext'
import { getAllExpandedKeys } from './utils/expandTableUtils'
import authorization from 'live/services/authorization'
import { unmountWellHierarchy } from './utils/unmountHierarchy'

const hasPermissionToEdit =
    authorization.superAdmin ||
    authorization.admin ||
    authorization.Permission.ADMIN ||
    authorization.Permission.OGPERFORMANCE_MANAGE_KPI ||
    authorization.Permission.OGPERFORMANCE_ADMINISTRATE_KPI

const ImporterApp = (): JSX.Element => {
    const [showExportStaticDataModal, setShowExportStaticDataModal] = useState<boolean>(false)
    const [isImportDataSideBarOpen, setIsImportDataSideBarOpen] = useState(false)
    const [localErrors, setLocalErrors] = useState<LocalErrorsTypes[]>([])
    const [hierarchyErrors, setHierarchyErrors] = useState<HierarchyErrorsTypes[]>([])
    const [isFetchingForLocalValidation, setIsFetchingForLocalValidation] = useState(false)
    const [isFetchingForHierarchicalValidation, setIsFetchingForHierarchicalValidation] = useState(false)
    const [isFetchingForTransferValidation, setIsFetchingForTransferValidation] = useState(false)
    const [showDecisionModal, setShowDecisionModal] = useState(false)
    const [hasEntityChecked, setHasEntityChecked] = useState(false)
    const [isErrorsSidebarOpen, setIsErrorsSidebarOpen] = useState<boolean>(false)

    const {
        data,
        setData,
        checkedToTransfer,
        isValidToTransfer,
        setIsValidToTransfer,
        updateValueFromCheckedEntity,
        liveRigs,
        liveWells,
        isFetching,
        setIsFetching,
        optionsOfWellEntity,
        setTemporaryData,
        expandedRowKeys,
        setExpandedRowKeys,
        initialExpandedRowKeys,
        setExpandedRowKeysByEntity,
        hasSomeExpandedRows
    } = useContext(StaticDataImporterContext)

    useEffect(() => {
        setIsErrorsSidebarOpen(localErrors.length > 0 || hierarchyErrors.length > 0)
    }, [localErrors, hierarchyErrors])

    useEffect(() => {
        const has = checkedToTransfer && Object.keys(checkedToTransfer).some(key => checkedToTransfer[key].length > 0)
        setHasEntityChecked(has)
    }, [checkedToTransfer])

    const passOnlySelectedEntities = useCallback(
        (entities: TemporaryDataType, entityKeys: string[]): TemporaryDataType | null => {
            let selectedEntities = null

            for (const key of entityKeys) {
                if (entities[key].length > 0) {
                    selectedEntities = { ...selectedEntities, [key]: entities[key] }
                }
            }

            if (!selectedEntities && !isErrorsSidebarOpen) {
                globalNotification.error(
                    <GlobalNotification
                        title="It was not possible to validate"
                        description="You need to select some entity"
                        type="error"
                    />
                )

                return selectedEntities
            }

            return selectedEntities
        },
        [isErrorsSidebarOpen]
    )

    const getValueFromLabel = (options: SelectOptions[], label: string): string | undefined => {
        if (!options?.length) return

        return options.find(
            option =>
                option?.label?.toLowerCase() === label?.toLowerCase() ||
                option?.value?.toLowerCase() === label?.toLowerCase()
        )?.value
    }

    const validateFieldsByEntities = useCallback(
        async (entities: TemporaryDataType): Promise<LocalErrorsTypes[]> => {
            try {
                setIsFetchingForLocalValidation(true)

                const entityKeys = Object.keys(entities)
                const selectedEntities = passOnlySelectedEntities(entities, entityKeys)
                if (!selectedEntities) return

                const errors = []
                const errorFields = await validateLocalFieldsByEntity(selectedEntities)
                if (!errorFields) {
                    setIsFetchingForLocalValidation(false)

                    return errors
                }
                const hasLocalError = Object.keys(errorFields).some(key => Object.keys(errorFields[key]).length > 0)

                if (hasLocalError) {
                    for (const frontUuid of Object.keys(errorFields)) {
                        for (const errorMap of errorFields[frontUuid]) {
                            for (const [columnName, message] of Object.entries(errorMap)) {
                                let entityName = ''
                                let rowEntityId = null
                                for (const key of Object.keys(selectedEntities)) {
                                    const rowId = selectedEntities[key].find(entity => entity.frontUuid === frontUuid)
                                        ?.rowId

                                    if (rowId) {
                                        entityName = key
                                        rowEntityId = rowId
                                        break
                                    }
                                }
                                errors.push({ uuid: frontUuid, entityName, columnName, rowId: rowEntityId, message })
                            }
                        }
                    }
                }

                setLocalErrors(errors)

                return errors
            } catch (error) {
                console.error(error)
                setLocalErrors([])
                setIsFetchingForLocalValidation(false)
            }
        },
        [passOnlySelectedEntities]
    )

    const validateEntitiesByHierarchy = useCallback(
        async (
            entities: TemporaryDataType,
            hierarchicalErrorsByTransfer: Record<string, string> = null
        ): Promise<LocalErrorsTypes[]> => {
            try {
                setIsFetchingForHierarchicalValidation(true)

                const entityKeys = Object.keys(entities)
                const selectedEntities = passOnlySelectedEntities(entities, entityKeys)
                if (!selectedEntities) return

                const errors = []
                const hierarchicalErrors =
                    hierarchicalErrorsByTransfer ?? (await validateEntityByHierarchy(selectedEntities))
                if (!hierarchicalErrors) {
                    setIsFetchingForHierarchicalValidation(false)

                    return errors
                }
                const hasHierarchicalError = Object.keys(hierarchicalErrors).some(
                    key => hierarchicalErrors[key].length > 0
                )

                if (hasHierarchicalError) {
                    for (const uuid of Object.keys(hierarchicalErrors)) {
                        const message = hierarchicalErrors[uuid]
                        let entityName = ''
                        let rowEntityId = null
                        for (const key of Object.keys(selectedEntities)) {
                            const rowId = selectedEntities[key].find(entity => entity.uuid === uuid)?.rowId

                            if (rowId) {
                                entityName = key
                                rowEntityId = rowId
                                break
                            }
                        }
                        errors.push({ uuid, entityName, rowId: rowEntityId, message })
                    }
                }
                setHierarchyErrors(errors)

                return errors
            } catch (error) {
                console.error(error)
                setHierarchyErrors([])
                setIsFetchingForHierarchicalValidation(false)
            }
        },
        [passOnlySelectedEntities]
    )

    const validateDataForExport = useCallback(
        async (entities: TemporaryDataType, isClickToTransfer?: boolean): Promise<void> => {
            removeAllActiveErrorClassFromRows()
            setIsValidToTransfer(false)
            setLocalErrors([])
            setHierarchyErrors([])

            if (!entities || !Object.keys(entities).some(key => entities[key].length > 0)) return

            entities = { ...entities }
            entities = unmountWellHierarchy(entities)

            validateFieldsByEntities(entities).then(async localFieldErrors => {
                setIsFetchingForLocalValidation(false)
                if (localFieldErrors?.length > 0) return

                const well = entities.wells[0]
                if (!well) return
                const codafWellList = await fetchCodafEntity('well')
                const currentCodafWell = codafWellList.find(codafWell => codafWell.assetId === well?.assetId)

                const hasCurrentWellOnCodaf = currentCodafWell != undefined

                const { waterDepthAsAttributeUnit, wellIdInCodaf, ...codafWell } = well

                let newWellFromCodaf: WellType = null

                if (!hasCurrentWellOnCodaf) {
                    const newCodafWell = {
                        ...codafWell,
                        waterDepth: {
                            id: null,
                            unitOfMeasure_id: 1,
                            name: 'm',
                            value: codafWell.waterDepth ? codafWell.waterDepth : null
                        },
                        wellType: getValueFromLabel(optionsOfWellEntity.wellType, codafWell.wellType),
                        wellCategory: getValueFromLabel(optionsOfWellEntity.wellCategory, codafWell.wellCategory),
                        referenceSystem: getValueFromLabel(
                            optionsOfWellEntity.referenceSystem,
                            codafWell.referenceSystem
                        )
                    }
                    newWellFromCodaf = await createWellOnCodaf(newCodafWell)
                }

                entities.wells[0].wellIdInCodaf = newWellFromCodaf?.id ?? wellIdInCodaf
                entities.wells[0].uuid = newWellFromCodaf?.uuid ?? currentCodafWell?.uuid
                entities.wells[0].waterDepthAsAttributeUnit =
                    (newWellFromCodaf?.waterDepth as AttributeUnit) ?? waterDepthAsAttributeUnit

                if (isClickToTransfer) {
                    setIsFetchingForTransferValidation(true)
                    transferDraftTableToStaticDataDashboard(entities)
                        .then(res => {
                            if (Object.keys(res).length) {
                                validateEntitiesByHierarchy(entities, res)
                                    .then(() => {
                                        if (!hasCurrentWellOnCodaf && newWellFromCodaf) {
                                            deleteWellFromCodaf(newWellFromCodaf.id)
                                        }
                                    })
                                    .finally(() => setIsFetchingForHierarchicalValidation(false))

                                return
                            }

                            globalNotification.success(
                                <GlobalNotification title="The data was successfully transferred to Static Data Dashboard." />,
                                7
                            )

                            setShowDecisionModal(true)
                            setIsValidToTransfer(true)

                            if (hasCurrentWellOnCodaf) {
                                const newCodafWell = {
                                    ...codafWell,
                                    waterDepth: {
                                        ...waterDepthAsAttributeUnit,
                                        value: Number(codafWell.waterDepth)
                                    },
                                    wellType: getValueFromLabel(optionsOfWellEntity.wellType, codafWell.wellType),
                                    wellCategory: getValueFromLabel(
                                        optionsOfWellEntity.wellCategory,
                                        codafWell.wellCategory
                                    ),
                                    referenceSystem: getValueFromLabel(
                                        optionsOfWellEntity.referenceSystem,
                                        codafWell.referenceSystem
                                    )
                                }
                                updateWellFromCodaf(newCodafWell)
                            }
                        })
                        .finally(() => {
                            setIsFetchingForTransferValidation(false)
                        })

                    return
                }

                validateEntitiesByHierarchy(entities)
                    .then(hierarchicalErrors => {
                        if (!hierarchicalErrors.length) {
                            globalNotification.success(
                                <GlobalNotification
                                    title="All validations are ok!"
                                    description="The data is ready to be transferred"
                                />,
                                7
                            )
                        }
                        setIsValidToTransfer(!hierarchicalErrors.length)
                    })
                    .finally(() => {
                        if (!hasCurrentWellOnCodaf && newWellFromCodaf) {
                            deleteWellFromCodaf(newWellFromCodaf.id)
                        }
                        setIsFetchingForHierarchicalValidation(false)
                    })
            })
        },
        [
            optionsOfWellEntity?.referenceSystem,
            optionsOfWellEntity?.wellCategory,
            optionsOfWellEntity?.wellType,
            setIsValidToTransfer,
            validateEntitiesByHierarchy,
            validateFieldsByEntities
        ]
    )

    const handleEditData = (entity: WellDto[], row: WellDto, setter: TemporarySetDataType): void => {
        const newData = [...entity]
        const index = newData.findIndex(item => row.rowId === item.rowId)
        const item = newData[index]
        if (item) {
            saveAllTemporaryData(row, 'WELL').then(saves => {
                row.rowId = saves[0].id
                row.key = saves[0].id
                newData.splice(index, 1, {
                    ...item,
                    ...row
                })
                setter.setWells(newData)
                setIsValidToTransfer(false)
                updateValueFromCheckedEntity(`wells=>${row.parent}`, row)
            })
        }
    }

    const handleEditSave = (row: WellDto): void => {
        handleEditData(data.wells, row, setData)
    }

    const handleTransferDataToStaticData = (): void => {
        validateDataForExport(checkedToTransfer, true)
    }

    const removeErrorWhenCheckedRowIsRemoved = (newCheckedData: TemporaryDataType): void => {
        // Diasble this function because it is not being used
        // Ticket: https://intelie.atlassian.net/browse/OGP21-3931
        // validateDataForExport(newCheckedData)
    }

    const removeAllActiveErrorClassFromRows = (): void => {
        requestAnimationFrame(() => {
            const rows = document.querySelectorAll('.active-error')
            if (rows.length === 0) return

            // Usar textContent para trigger batch update
            rows.forEach(row => row.classList.remove('active-error'))
        })
    }

    const handleExpandAll = (): void => {
        if (hasSomeExpandedRows) {
            setExpandedRowKeys(initialExpandedRowKeys)
        } else {
            getAllExpandedKeys(data, initialExpandedRowKeys, setExpandedRowKeysByEntity)
        }
    }

    return (
        <>
            <ImporterContainer>
                <HeaderAndTableContainer>
                    {isFetching ? (
                        <LoadingOverlay className="loading-overlay">
                            <>
                                <Loading size="sm" show={true} />
                                <span style={{ marginLeft: '4px' }}>Loading...</span>
                            </>
                        </LoadingOverlay>
                    ) : !data.wells.length ? (
                        <NoData
                            hasPermissionToEdit={!!hasPermissionToEdit}
                            setData={setData}
                            setIsImportDataSideBarOpen={setIsImportDataSideBarOpen}
                        />
                    ) : (
                        <>
                            <ImporterHeader>
                                <ActionButtonsContainer>
                                    <button
                                        className="import-data-btn secondary-btn"
                                        onClick={() => setIsImportDataSideBarOpen(true)}
                                    >
                                        <i className="fa fal fa-file-download"></i>
                                        {i18n('Import Data')}
                                    </button>
                                    <button
                                        className="validate-btn secondary-btn"
                                        onClick={() =>
                                            (hasEntityChecked || isErrorsSidebarOpen) &&
                                            validateDataForExport(checkedToTransfer)
                                        }
                                        disabled={
                                            (!hasEntityChecked && !isErrorsSidebarOpen) ||
                                            isFetchingForLocalValidation ||
                                            isFetchingForHierarchicalValidation ||
                                            isFetchingForTransferValidation
                                        }
                                    >
                                        <Loading
                                            size="sm"
                                            show={isFetchingForLocalValidation || isFetchingForHierarchicalValidation}
                                        />
                                        {i18n('Validate Entities')}
                                        {isValidToTransfer && <i className="far fa-check-circle" />}
                                    </button>
                                    <button
                                        className="transfer-data-btn secondary-btn"
                                        onClick={() => handleTransferDataToStaticData()}
                                        disabled={
                                            (!hasEntityChecked && !isErrorsSidebarOpen) ||
                                            isFetchingForLocalValidation ||
                                            isFetchingForHierarchicalValidation ||
                                            isFetchingForTransferValidation
                                        }
                                    >
                                        <i className="fas fa-file-export"></i>
                                        {i18n('Transfer to Static Data')}
                                    </button>
                                    <button
                                        className="import-data-btn secondary-btn"
                                        onClick={() => setShowExportStaticDataModal(true)}
                                    >
                                        <i className="fas fa-download"></i>
                                        {i18n('Download Static Data')}
                                    </button>
                                    <button className="secondary-btn" onClick={handleExpandAll}>
                                        <i
                                            className={`fas ${
                                                hasSomeExpandedRows ? 'fas fa-angle-up' : 'fas fa-angle-down'
                                            }`}
                                        ></i>
                                        {hasSomeExpandedRows ? i18n('Collapse all') : i18n('Expand all')}
                                    </button>
                                </ActionButtonsContainer>
                            </ImporterHeader>
                            <TemporaryDataTable
                                data={data}
                                setData={setData}
                                localErrors={localErrors}
                                removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                                isErrorsSidebarOpen={isErrorsSidebarOpen}
                                liveWells={liveWells}
                                liveRigs={liveRigs}
                                optionsOfWellEntity={optionsOfWellEntity}
                                handleEditSave={handleEditSave}
                            />
                        </>
                    )}
                    {isImportDataSideBarOpen && (
                        <ImportData
                            setIsImportDataSideBarOpen={setIsImportDataSideBarOpen}
                            setTemporaryData={setTemporaryData}
                            setIsFetching={setIsFetching}
                        />
                    )}
                </HeaderAndTableContainer>

                {isErrorsSidebarOpen && (
                    <DataErrorsSidebar
                        localErrors={localErrors}
                        hierarchyErrors={hierarchyErrors}
                        setIsErrorsSidebarOpen={setIsErrorsSidebarOpen}
                        setExpandedRowKeysByEntity={setExpandedRowKeysByEntity}
                        handleExpandAll={handleExpandAll}
                    />
                )}

                <DecisionModal
                    showDecisionModal={showDecisionModal}
                    setShowDecisionModal={setShowDecisionModal}
                    data={data}
                    setData={setData}
                />
            </ImporterContainer>

            <ExportStaticDataModal show={showExportStaticDataModal} setShow={setShowExportStaticDataModal} />
        </>
    )
}

export default ImporterApp

const ImporterContainer = styled.div`
    display: flex;
    height: calc(100% - 48px);
`

const HeaderAndTableContainer = styled.div`
    flex: 1;
    padding: 16px;
    position: relative;

    overflow-y: hidden;

    .loading-overlay {
        z-index: 2;
    }
`

const ImporterHeader = styled.header`
    padding: 16px 0 32px;
    display: flex;
    flex-direction: column;

    border-bottom: 1px solid var(--gray-400);
`

const ActionButtonsContainer = styled.div`
    display: flex;
    justify-content: end;
    gap: 8px;

    .secondary-btn {
        display: flex;
        align-items: center;
        gap: 4px;

        padding: 0 12px;
        width: fit-content;
        min-width: 75px;
        height: 30px;

        font-size: 12px;
        font-weight: 700;

        border-radius: 4px;
        border: none;

        border: 1px solid var(--gray-400);
        background-color: transparent;

        &:hover:not(:disabled) {
            opacity: 0.8;
        }

        &:disabled {
            opacity: 0.6;
        }

        .fa-chevron-left {
            margin-right: 4px;
        }

        .fa-check-circle {
            color: var(--green-700);
        }
    }
`

const ProgressBarWrapper = styled.div`
    width: 60px;
    height: 20px;
    background-color: var(--gray-200);
    border-radius: 2px;
    overflow: hidden;
    border: 1px solid var(--gray-400);
    position: relative;
`

const ProgressBar = styled.div<{ percentage: number }>`
    height: 100%;
    width: ${props => props.percentage}%;
    background: linear-gradient(90deg, var(--blue-500), var(--blue-700));
    transition: width 0.3s ease;
    border-radius: 2px;
`
